import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const AUTHOR_ROLES = ["advisor", "accelerator", "investor", "provider", "admin"];
const VIS_BADGE = { private: "muted", connections: "accent", public: "good" };

export default function FrameworksList() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/frameworks")
      .then((r) => setItems(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;
  if (items === null) return <div className="tb-loading">{t("common.loading")}</div>;

  const isAuthor = AUTHOR_ROLES.includes(user.role);
  const mine = items.filter((f) => f.owner_id === user.id);
  const shared = items.filter((f) => f.owner_id && f.owner_id !== user.id);
  const system = items.filter((f) => !f.owner_id);

  const Card = ({ f }) => {
    const title = lang === "fr" ? f.title_fr || f.title_en : f.title_en;
    const desc = lang === "fr" ? f.description_fr || f.description_en : f.description_en;
    const canEdit = user.role === "admin" || f.owner_id === user.id;
    return (
      <div className="tb-block" data-testid={`fw-card-${f.id}`}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
          <strong style={{ fontSize: "1rem" }}>{title}</strong>
          {f.owner_id && <span className={`tb-badge ${VIS_BADGE[f.visibility] || "muted"}`}>{t(`fwBuilder.vis${f.visibility?.[0]?.toUpperCase()}${f.visibility?.slice(1)}` ) || f.visibility}</span>}
          {!f.owner_id && <span className="tb-badge muted">{t("fwList.system")}</span>}
        </div>
        {f.author_name && <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 4 }}>{t("fwList.by")} {f.author_name}</div>}
        {desc && <p className="tb-muted" style={{ fontSize: "0.85rem", marginTop: 8 }}>{desc.length > 160 ? desc.slice(0, 160) + "…" : desc}</p>}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {canEdit && f.owner_id && (
            <Link to={`/app/frameworks/build/${f.id}`} className="tb-ghost-btn" style={{ textDecoration: "none", padding: "6px 12px", fontSize: "0.82rem" }} data-testid={`fw-edit-${f.id}`}>
              {t("fwList.edit")}
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="tb-page" data-testid="frameworks-page">
      <h1 className="tb-h1">{t("fwList.title")}</h1>
      <p className="tb-muted" style={{ maxWidth: 760, marginBottom: "3.5rem" }}>{t("fwList.subtitle")}</p>

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}

      {isAuthor && (
        <div className="tb-fw-actions">
          <Link to="/app/frameworks/build" className="tb-primary-btn" data-testid="fw-new-btn">
            + {t("fwList.create")}
          </Link>
          <Link to="/app/frameworks/import" className="tb-ghost-btn" data-testid="fw-import-btn">
            ↓ {t("fwList.import")}
          </Link>
        </div>
      )}

      {isAuthor && (
        <>
          <h2 className="tb-h2" style={{ marginTop: "3.25rem" }}>{t("fwList.mine")}</h2>
          {mine.length === 0 ? (
            <p className="tb-muted" data-testid="fw-mine-empty">{t("fwList.mineEmpty")}</p>
          ) : (
            <div className="tb-card-grid" data-testid="fw-mine-grid">{mine.map((f) => <Card key={f.id} f={f} />)}</div>
          )}
        </>
      )}

      <h2 className="tb-h2">{t("fwList.shared")}</h2>
      {shared.length === 0 ? (
        <p className="tb-muted">{t("fwList.sharedEmpty")}</p>
      ) : (
        <div className="tb-card-grid">{shared.map((f) => <Card key={f.id} f={f} />)}</div>
      )}

      <h2 className="tb-h2">{t("fwList.library")}</h2>
      <div className="tb-card-grid">{system.map((f) => <Card key={f.id} f={f} />)}</div>
    </div>
  );
}
